import type { CellContext, Column, ColumnDef } from '@tanstack/react-table';
import { TokenBalanceSummary } from 'graphql/codegen/graphql';
import { LuLock } from 'react-icons/lu';
import { formatDecimalValue, tokenNanosToInteger } from 'utils/currency';
import { getUserName } from 'utils/user';
import LowNumFormatter from './LowNumFormatter';
import { renderSortingHeader } from './TableSortingHeader';
import { Progress } from '../shadcn/ui/progress';
import { AccountSocialCard } from './AccountSocialCard';
import { Avatar } from '../core/Avatar';
import { RouteLink } from '../core/RouteLink';
import { shortenLongWord } from '../../utils/deso';
import React from 'react';

interface ColumnProps {
  holdersPercentageMap: Record<string, number>;
  usdValueMap: Record<string, number>;
}

interface CustomCellContext extends CellContext<TokenBalanceSummary, unknown> {
  refetchData?: () => void;
}

export const tableColumns = ({ holdersPercentageMap, usdValueMap }: ColumnProps): ColumnDef<TokenBalanceSummary>[] => {
  return [
    {
      accessorKey: 'id',
      header: () => <span className="flex items-center text-xs text-muted">#</span>,
      cell: ({ row }: CustomCellContext) => {
        const { id } = row;
        return <span className="text-xs text-muted">{+id + 1}</span>;
      },
    },
    {
      accessorKey: 'hodler.publicKey',
      header: () => <span className="flex items-center text-xxs">Holder</span>,
      cell: ({ row }: CustomCellContext) => {
        const h = row.original;
        return (
          <AccountSocialCard publicKey={h.hodler?.publicKey}>
            <div className="flex items-center py-2 pl-2">
              <Avatar border="none" className="flex-shrink-0 bg-gray-faint" src={h.hodler?.publicKey} />
              <div className="ml-2 truncate text-ellipsis">
                <div className="text-muted-foreground text-xs font-semibold flex items-center text-left">
                  <RouteLink to={`/u/${h.hodler?.username ?? h.hodler?.publicKey}`} className="w-full">
                    <div className="hover:underline truncate">{getUserName({ account: h.hodler })}</div>
                  </RouteLink>
                </div>

                <div className="hidden md:block text-xs text-muted text-left">
                  {shortenLongWord(h.hodler?.publicKey ?? '', 6, 6)}
                </div>
              </div>
            </div>
          </AccountSocialCard>
        );
      },
    },
    {
      accessorKey: 'totalBalance',
      header: ({ column }: { column: Column<TokenBalanceSummary, unknown> }) =>
        renderSortingHeader({
          column,
          title: 'Coins Held',
          className: 'text-xxs',
        }),
      cell: ({ row }: CustomCellContext) => {
        const lockedBalanceNanos = Number(row.original.lockedBalanceNanos ?? '0');
        return (
          <div className="flex flex-col gap-1 text-left text-sm">
            <div>
              <LowNumFormatter
                price={tokenNanosToInteger(row.original?.totalBalance || 0)}
                isUsd={false}
                highPriceThreshold={10}
                className="font-mono text-sm text-muted-foreground"
              />
              {row.original.hodler?.publicKey && usdValueMap[row.original.hodler.publicKey] && (
                <div className="text-green-500 text-xs">
                  ~
                  <LowNumFormatter
                    price={usdValueMap[row.original.hodler?.publicKey] ?? 0}
                    abbreviatePriceThreshold={1}
                    className="font-mono text-green-500 text-xs"
                  />{' '}
                  USD
                </div>
              )}
            </div>
            {lockedBalanceNanos > 0 && (
              <div className="flex items-center gap-1 text-muted">
                <LuLock />
                <LowNumFormatter price={tokenNanosToInteger(lockedBalanceNanos)} className="font-mono" isUsd={false} />
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'ownership',
      header: () => <span className="flex items-center text-xxs">Ownership</span>,
      cell: ({ row }: CustomCellContext) => {
        const percentage = holdersPercentageMap[row.original.hodler?.publicKey as string];
        const progressValue = Math.max(percentage, 1);

        return (
          <div className="relative min-w-24 py-3">
            <div className="mb-1 text-left font-mono text-sm text-muted-foreground">
              {formatDecimalValue(percentage)}%
            </div>
            <Progress value={progressValue} className="h-1 w-full bg-card" indicatorClassName="bg-muted-foreground" />
          </div>
        );
      },
    },
  ];
};
